// Failed and cancelled-before-dispatch views (DESIGN_SPEC §11.4.2, §11.4.14).
//   <TerminalLens ctx facts />    no hero: the job state glyph + word over a sheet (failed: phase, after, reason,
//                                 whether the sample ran · cancelled before dispatch: cancelled at, phase)
//   <FailedActions ctx />         Run again (fetches the stored sample, resubmits with the same spec; Turnstile when
//                                 the controller asks for it) · Download sample (presigned, else same-origin)
//   <TerminalBody ctx plan />     only the figures that have data, then one line saying why there is nothing else
import { Alert, JobState, Kv, Lens, Time, buttonClass, type KvRow } from "@/components/ui";
import { storedFile, useArmedSubmit } from "@/features/submit/useArmedSubmit";
import { pickArtifact, usePresignedDownload } from "@/hooks";
import type { SubmitSpec } from "@/lib/api";
import { clock } from "@/lib/format";
import type { ReportCtx } from "@/lib/derive/types";
import type { Artifact, Job } from "@/types";
import type { ReportFacts } from "./facts";
import type { DocumentPlan } from "./figOrder";
import { useThirdPartyRow } from "./panels/ThirdPartyFig";
import { ReportBody } from "./ReportBody";

export function TerminalLens({ ctx, facts }: { ctx: ReportCtx; facts: ReportFacts }) {
  const { job } = ctx;
  const failed = facts.state === "failed";
  const started = job.dispatched_at ? Date.parse(job.dispatched_at) : NaN;
  const after = facts.endedAt !== null && Number.isFinite(started) && facts.endedAt >= started ? clock(facts.endedAt - started, 1) : null;
  const thirdParty = useThirdPartyRow(job);
  const rows: KvRow[] = failed
    ? [
        ["Phase", facts.phaseDisplay],
        ["After", after ? <>{after} <small>from dispatch</small></> : null],
        ["Reason", facts.reason ? <span className="whitespace-normal text-left wrap-anywhere" translate="no">{facts.reason}</span> : null],
        ["Sample", facts.sampleStarted ? "started" : <span className="text-ink-faint">never ran</span>],
        ...(thirdParty ? [thirdParty] : []),
      ]
    : [
        ["Cancelled at", facts.endedAt !== null ? <><Time value={facts.endedAt} mode="clock" /> <small>UTC</small></> : null],
        ["Phase", "queued"],
        ...(thirdParty ? [thirdParty] : []),
      ];
  return (
    <Lens label={failed ? "Under the lens: failure" : "Under the lens: cancellation"} eyebrow={failed ? "Under the lens · failure" : "Under the lens · cancellation"} className="rhead-lens">
      <p className="py-1 [&_.st]:text-[13px]">
        <JobState s={failed ? "failed" : "cancelled"} note={failed ? `during ${facts.phaseDisplay}` : "before dispatch"} />
      </p>
      <Kv rows={rows} />
    </Lens>
  );
}

function specOf(job: Job): SubmitSpec {
  const spec: SubmitSpec = { profile: job.profile, network: job.network, timeout_secs: job.timeout_secs };
  if (job.command) spec.command = job.command;
  if (job.labels?.length) spec.labels = job.labels;
  if (job.allow_input) spec.allow_input = true;
  if (job.auto_analyze_children) spec.auto_analyze_children = true;
  return spec;
}

function RunAgain({ job, sample }: { job: Job; sample: Artifact }) {
  const run = useArmedSubmit(async () => ({ file: await storedFile(job.id, sample.sha256, job.filename || sample.name), spec: specOf(job) }), `/jobs/${job.id}`);
  return (
    <>
      <button type="button" className={buttonClass({ variant: "primary" })} disabled={run.working} onClick={run.arm}>
        {run.working ? <span className="breathe">{run.checking ? "Checking you're human…" : "Submitting…"}</span> : "Run again"}
      </button>
      {run.working && run.check && <div ref={run.widget} className="basis-full empty:hidden" />}
      {run.error && (
        <Alert
          head={run.error.head}
          className="basis-full"
          actions={
            run.error.login ? (
              <a className="lnk" href={run.signIn}>
                Sign in with GitHub
              </a>
            ) : undefined
          }
        >
          {run.error.detail}
        </Alert>
      )}
    </>
  );
}

function DownloadSample({ job, sample }: { job: Job; sample: Artifact }) {
  const { download, pending } = usePresignedDownload();
  return (
    <button type="button" className={buttonClass({ variant: "secondary" })} onClick={() => void download(job.id, { sha256: sample.sha256, name: job.filename || sample.name })}>
      {pending === sample.sha256 ? <span className="breathe">Preparing…</span> : "Download sample"}
    </button>
  );
}

export function FailedActions({ ctx }: { ctx: ReportCtx }) {
  const sample = pickArtifact(ctx.artifacts, "sample");
  if (!sample || ctx.job.kind !== "file") return null;
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2.5">
      <RunAgain job={ctx.job} sample={sample} />
      <DownloadSample job={ctx.job} sample={sample} />
    </div>
  );
}

export function TerminalBody({ ctx, plan, facts }: { ctx: ReportCtx; plan: DocumentPlan; facts: ReportFacts }) {
  const note =
    facts.state === "failed"
      ? facts.sampleStarted
        ? null
        : plan.rows.length
          ? "nothing else recorded · the sample never ran"
          : "nothing recorded · the sample never ran · no stored files"
      : plan.rows.length
        ? "nothing else recorded · cancelled before dispatch"
        : "nothing recorded · cancelled before dispatch";
  return <ReportBody ctx={ctx} plan={plan} index={false} note={note} />;
}
